<template>
  <article v-if="ctx.subscription" class="subscription-modify-view">
    <div>
      <router-link :to="{ name: 'settings-subscriptions' }">
        <lucide-icon name="chevron-left" /> {{ $t("objectNames.subscription", 40) }}
      </router-link> /
      <router-link :to="{ name: 'subscription-details', params: { subscriptionId } }">
        {{ $t(`subscriptions.${ctx.subscription?.plan}.name`) }}
      </router-link> /
      {{ $t("actions.modifySubscription") }}
    </div>

    <div class="subscription-modify-view__main mt-5">
      <div class="subscription-modify-view__sheet">
        <template v-if="mode == 'choose'">
          <div class="subscription-modify-view__header">
            <h2 class="mt-0">{{ $t("actions.modifySubscription") }}</h2>
            <p>
              {{ $t("texts.modifySubscription") }}
            </p>
          </div>

          <div class="flex gap-4">
            <section class="background-dark p-4">
              <h3 class="mt-0">{{ $t("headings.currentSubscriptionPeriod") }}</h3>
              {{ $t("labels.countUsers", ctx.subscription.seats || 0) }}
              <div v-for="upgrade of ctx.subscription.upgrades" :key="upgrade">
                {{ $t(`upgrades.${upgrade}.name`) }}
              </div>
            </section>

            <div class="text-18 pt-4">
              <lucide-icon name="arrow-right" />
            </div>

            <section v-if="ctx.subscription.nextRenew" class="background-dark p-4">
              <h3 class="mt-0">{{ $t("headings.nextSubscriptionPeriod") }}</h3>
              {{ $t("labels.countUsers", ctx.subscription.nextRenew.seats || 0) }}
              <div v-for="upgrade of ctx.subscription.nextRenew.upgrades" :key="upgrade">
                {{ $t(`upgrades.${upgrade}.name`) }}
              </div>
            </section>
          </div>

          <div class="mt-auto subscription-modify-view__buttonsrow">
            <router-link v-if="ctx.subscription.editable && ctx.subscription.plan == 'organization'" :to="{ name: 'subscription-upgrade' }" class="button">
              {{ $t("actions.upgradeSubscription") }}
            </router-link>
            <template v-if="ctx.subscription.renewAutomatically">
              <router-link v-if="ctx.subscription.editable && ctx.subscription.plan == 'organization'" :to="{ name: 'subscription-downgrade' }" class="button button_secondary">
                {{ $t("actions.downgradeSubscription") }}
              </router-link>
              <button type="button" class="button button_secondary" @click="cancelSubscription">
                {{ $t("actions.cancelShort") }}
              </button>
            </template>
            <button v-if="!ctx.subscription.renewAutomatically" type="button" class="button button_secondary" @click="reactivateSubscriptionRenewal">
              {{ $t("actions.enableRenewal") }}
            </button>
          </div>
        </template>

        <template v-if="mode == 'upgrade' && ctx.plan">
          <template v-if="readyToUpgrade">
            <div class="subscription-modify-view__header">
              <h2 class="mt-0">{{ $t("actions.upgradeSubscription") }}</h2>
              <p class="mv-0">{{ $t("texts.upgradeDisclaimer", { remainingDays: ctx.subscription.remainingDays }) }}</p>
            </div>

            <h3 class="mv-0">{{ $t("headings.overview") }}</h3>
            <div class="flex gap-4">
              <section class="background-dark p-4">
                <h3 class="mt-0">{{ $t("headings.currentSubscriptionPeriod") }}</h3>
                {{ $t("labels.countUsers", ctx.subscription.seats || 0) }}
                <div v-for="upgrade of ctx.subscription.upgrades" :key="upgrade">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </section>

              <div class="text-18 pt-4">
                <lucide-icon name="arrow-right" />
              </div>

              <section class="background-dark p-4">
                <h3 class="mt-0">{{ $t("headings.nextSubscriptionPeriod") }}</h3>
                {{ $t("labels.countUsers", nextPeriodConfig.seats) }}
                <div v-for="upgrade of nextPeriodConfig.upgrades" :key="upgrade">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </section>
            </div>

            <div>
              <h3 class="mt-2">
                {{ $t("headings.nowDue") }}
              </h3>
              <total-costs
                v-if="ctx.plan && paidUpgrade"
                :plan="ctx.plan"
                :seats="paidUpgrade.seats"
                :upgrades="paidUpgrade.upgrades"
                :days="ctx.subscription.remainingDays"
                :include-vat="includeVat"
                :vat="VAT"
                :borderless="true"
                :compact="true"
                :per-year="false"
              />
              <div v-else>
                {{ $t("texts.unpaidUpgrade") }}
              </div>
            </div>

            <div class="flex justify-between">
              <button class="button button_secondary" type="button" @click="readyToUpgrade = false">
                {{ $t("labels.back") }}
              </button>

              <button class="button" type="button" @click="sendOrder('upgrade')" :disabled="ordering">
                {{ $t("actions.submitOrder") }} <loading-spinner v-if="ordering" size="small" class="ml-2" />
              </button>
            </div>
          </template>
          <template v-else>
            <div class="subscription-modify-view__header">
              <h2 class="mt-0">{{ $t("actions.upgradeSubscription") }}</h2>
              <p class="mv-0">{{ $t("texts.upgradeDisclaimer", { remainingDays: ctx.subscription.remainingDays }) }}</p>
            </div>

            <div>
              <div class="label">
                {{ $t("labels.additionalUsers") }}
              </div>
              <div class="flex gap-4">
                <input-number
                  :value="subscriptionUpgrade.seats"
                  :min="0"
                  :step="1"
                  style="width: 5rem"
                  @update:value="subscriptionUpgrade.seats = $event"
                />
                <div class="whitespace-nowrap self-center">
                  {{ $t('labels.additionalUsers') }}
                </div>
              </div>
            </div>

            <div>
              <h3 class="label">{{ $t("headings.upgrades") }}</h3>

              <div class="r-50-50 gap-6">
                <subscription-upgrade
                  v-for="upgrade of nonTakenUpgrades"
                  :key="upgrade.id"
                  :upgrade="upgrade"
                  :selected="subscriptionUpgrade.upgrades.includes(upgrade.id)"
                  @select="addUpgradeUpgrade(upgrade.id)"
                />
              </div>
            </div>

            <h3 class="mb-0">{{ $t("headings.overview") }}</h3>

            <div class="flex gap-4">
              <section class="background-dark p-4">
                <h3 class="mt-0">{{ $t("headings.currentSubscriptionPeriod") }}</h3>
                {{ $t("labels.countUsers", ctx.subscription.seats || 0) }}
                <div v-for="upgrade of ctx.subscription.upgrades" :key="upgrade">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </section>

              <div class="text-18 pt-4">
                <lucide-icon name="arrow-right" />
              </div>

              <section class="background-dark p-4">
                <h3 class="mt-0">{{ $t("headings.nextSubscriptionPeriod") }}</h3>
                {{ $t("labels.countUsers", nextPeriodConfig.seats) }}
                <div v-for="upgrade of nextPeriodConfig.upgrades" :key="upgrade">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </section>
            </div>

            <div class="flex justify-between">
              <router-link :to="{ name: 'subscription-modify', params: { subscriptionId } }" class="button button_secondary">
                {{ $t("labels.back") }}
              </router-link>

              <button type="button" class="button" @click="readyToUpgrade = true">
                {{ $t("actions.continue") }}
              </button>
            </div>
          </template>
        </template>

        <template v-if="mode == 'downgrade'">
          <template v-if="readyToDowngrade">
            <div class="subscription-modify-view__header">
              <h2 class="mt-0">{{ $t("actions.downgradeSubscription") }}</h2>
              <p class="mv-0">{{ $t("texts.downgradeDisclaimer") }}</p>
            </div>

            <h3 class="mb-0">{{ $t("headings.overview") }}</h3>
            <div class="flex gap-4">
              <section class="background-dark p-4">
                <h3 class="mt-0">{{ $t("headings.currentSubscriptionPeriod") }}</h3>
                {{ $t("labels.countUsers", ctx.subscription.seats || 0) }}
                <div v-for="upgrade of ctx.subscription.upgrades" :key="upgrade">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </section>

              <div class="text-18 pt-4">
                <lucide-icon name="arrow-right" />
              </div>

              <section class="background-dark p-4">
                <h3 class="mt-0">{{ $t("headings.nextSubscriptionPeriod") }}</h3>
                {{ $t("labels.countUsers", nextPeriodConfig.seats) }}
                <div v-for="upgrade of nextPeriodConfig.upgrades" :key="upgrade">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </section>
            </div>


            <div>
              <h3>{{ $t("headings.cancelling") }}</h3>
              <section class="background-dark p-4">
                {{ $t("labels.countUsers", subscriptionDowngrade.seats) }}
                <div v-for="upgrade of subscriptionDowngrade.upgrades" :key="upgrade">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </section>
            </div>

            <div class="flex justify-between">
              <button type="button" class="button button_secondary" @click="readyToDowngrade = false">
                {{ $t("labels.back") }}
              </button>

              <button type="button" class="button" @click="sendOrder('downgrade')" :disabled="ordering">
                {{ $t("actions.downgradeSubscription") }} <loading-spinner v-if="ordering" size="small" class="ml-2" />
              </button>
            </div>
          </template>
          <template v-else>
            <div class="subscription-modify-view__header">
              <h2 class="mt-0">{{ $t("actions.downgradeSubscription") }}</h2>
              <p class="mv-0">{{ $t("texts.downgradeDisclaimer") }}</p>
            </div>

            <div>
              <div class="label">
                {{ $t("labels.usersToRemove") }}
              </div>
              <div class="flex gap-4">
                <input-number
                  :value="subscriptionDowngrade.seats"
                  :min="0"
                  :max="ctx.subscription.seats - (ctx.plan?.minSeats ?? 1)"
                  :step="1"
                  style="width: 5rem"
                  @update:value="subscriptionDowngrade.seats = $event"
                />
                <div class="whitespace-nowrap self-center">
                  / {{ ctx.subscription.seats }} {{ $t('labels.usersToRemove') }}
                </div>
              </div>
            </div>

            <div>
              <h3 class="label">{{ $t("headings.upgrades") }}</h3>

              <div class="r-50-50 gap-6">
                <subscription-upgrade
                  v-for="upgrade of takenUpgrades"
                  :key="upgrade.id"
                  :upgrade="upgrade"
                  :subscribed="true"
                  :selected="subscriptionDowngrade.upgrades.includes(upgrade.id)"
                  @select="addUpgradeDowngrade(upgrade.id)"
                />
              </div>
            </div>

            <h3 class="mb-0">{{ $t("headings.overview") }}</h3>

            <div class="flex gap-4">
              <section class="background-dark p-4">
                <h3 class="mt-0">{{ $t("headings.currentSubscriptionPeriod") }}</h3>
                {{ $t("labels.countUsers", ctx.subscription.seats || 0) }}
                <div v-for="upgrade of ctx.subscription.upgrades" :key="upgrade">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </section>

              <div class="text-18 pt-4">
                <lucide-icon name="arrow-right" />
              </div>

              <section class="background-dark p-4">
                <h3 class="mt-0">{{ $t("headings.nextSubscriptionPeriod") }}</h3>
                {{ $t("labels.countUsers", nextPeriodConfig.seats) }}
                <div v-for="upgrade of nextPeriodConfig.upgrades" :key="upgrade">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </section>
            </div>

            <div class="flex justify-between">
              <router-link :to="{ name: 'subscription-modify', params: { subscriptionId } }" class="button button_secondary">
                {{ $t("labels.back") }}
              </router-link>

              <button type="button" class="button" @click="readyToDowngrade = true" :disabled="subscriptionDowngrade.seats == 0 && subscriptionDowngrade.upgrades.length == 0">
                {{ $t("actions.continue") }}
              </button>
            </div>
          </template>
        </template>
      </div>

      <div class="p-4">
        <button class="mt-0 subscription-modify-view__priceheading" @click="lastPaymentCollapsed = !lastPaymentCollapsed">
          {{ $t("headings.lastPayment") }}
          <span class="subscription-modify-view__billingdate">{{ $t("texts.paidOn", { date: formatDateString(ctx.subscription.lastPaymentDate) }) }}</span>
          <lucide-icon :name="lastPaymentCollapsed ? 'chevron-down' : 'chevron-up'" class="ml-auto" />
        </button>
        <total-costs
          v-if="ctx.plan"
          :plan="ctx.plan"
          :seats="ctx.subscription.seats"
          :upgrades="ctx.subscription.upgrades"
          :include-vat="includeVat"
          :vat="VAT"
          :borderless="true"
          :compact="true"
          :per-year="false"
          :collapsed="lastPaymentCollapsed"
          class="pt-1"
        />

        <button class="mt-6 subscription-modify-view__priceheading" @click="nextPaymentCollapsed = !nextPaymentCollapsed">
          {{ $t("headings.nextPayment") }}
          <span class="subscription-modify-view__billingdate">{{ $t("texts.dueOn", { date: ctx.formattedRenewalDate }) }}</span>
          <lucide-icon :name="nextPaymentCollapsed ? 'chevron-down' : 'chevron-up'" class="ml-auto" />
        </button>
        <total-costs
          v-if="ctx.plan"
          :plan="ctx.plan"
          :seats="nextPeriodConfig.seats"
          :upgrades="nextPeriodConfig.upgrades"
          :years="nextPeriodConfig.years"
          :include-vat="includeVat"
          :vat="VAT"
          :borderless="true"
          :compact="true"
          :per-year="false"
          :collapsed="nextPaymentCollapsed"
          class="pt-1"
        />

        <div
          v-if="mode == 'upgrade' && paidUpgrade && (paidUpgrade.seats != 0 || paidUpgrade.upgrades.length != 0)"
          class="subscription-modify-view__now-due mt-6"
        >
          <h2 class="subscription-modify-view__priceheading mt-0 text-20">
            {{ $t("headings.nowDue") }}
          </h2>
          <total-costs
            v-if="ctx.plan"
            :plan="ctx.plan"
            :seats="paidUpgrade.seats"
            :upgrades="paidUpgrade.upgrades"
            :days="ctx.subscription.remainingDays"
            :include-vat="includeVat"
            :vat="VAT"
            :borderless="true"
            :compact="true"
            :per-year="false"
          />
        </div>
      </div>
    </div>
  </article>
  <div v-else>
    <loading-spinner />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { filter } from "lodash-es";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { formatDate } from "@/i18n";
import { useToast } from "vue-toastification";
import { showConfirm, showError, showMessage } from "@core/ui/alert/alert";

import { usePlans } from "@manager/plans/plans.store";

import LoadingSpinner from "@core/ui/LoadingSpinner.vue";
import InputNumber from "@core/input/InputNumber.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import SubscriptionUpgrade from "@manager/subscriptions/SubscriptionUpgrade.vue";
import TotalCosts from "@manager/purchasing/TotalCosts.vue";

import { useSubscriptions } from "@/api/subscriptions.store";
import { useSubscription } from "./useSubscription";

const {
  subscriptionId,
  mode = "choose"
} = defineProps<{
  subscriptionId: number;
  mode?: "choose" | "upgrade" | "downgrade";
}>();

const { t } = useI18n();
const toast = useToast();
const router = useRouter();

const { loadPlans, upgradePlans } = usePlans();
const subscriptionStore = useSubscriptions();

loadPlans();
subscriptionStore.getSubscriptions();

const ctx = useSubscription(() => subscriptionId);

const VAT = 0.19;

const lastPaymentCollapsed = ref(true);
const nextPaymentCollapsed = ref(mode == 'choose');


// =======================
// UPGRADE SUBSCRIPTION

const subscriptionUpgrade = reactive({
  seats: 0,
  upgrades: [] as string[]
});

const readyToUpgrade = ref(false);

const nonTakenUpgrades = computed(() => {
  return filter(upgradePlans, (upgrade) => {
    return !ctx.subscription?.upgrades.includes(upgrade.id) || !ctx.subscription.nextRenew.upgrades.includes(upgrade.id);
  });
});

function addUpgradeUpgrade(upgradeId: string) {
  if (subscriptionUpgrade.upgrades.includes(upgradeId)) {
    subscriptionUpgrade.upgrades = subscriptionUpgrade.upgrades.filter((id) => id !== upgradeId);
  } else {
    subscriptionUpgrade.upgrades.push(upgradeId);
  }
}

const paidUpgrade = computed(() => {
  if (ctx.subscription == null) return null;
  const sub = ctx.subscription;
  const plannedSeats = sub.nextRenew.seats + subscriptionUpgrade.seats;
  const seatsToPay = Math.max(plannedSeats - sub.seats, 0);

  const upgradesToPay = filter(upgradePlans, (upgrade) => {
    return !sub.upgrades.includes(upgrade.id) && subscriptionUpgrade.upgrades.includes(upgrade.id);
  }).map((upgrade) => upgrade.id);

  if (seatsToPay == 0 && upgradesToPay.length == 0) return null;

  return {
    seats: seatsToPay,
    upgrades: upgradesToPay
  }
});

// =======================
// DOWNGRADE SUBSCRIPTION

const subscriptionDowngrade = reactive({
  seats: 0,
  upgrades: [] as string[]
});

const readyToDowngrade = ref(false);

const takenUpgrades = computed(() => {
  const sub = ctx.subscription;
  if (sub == null) return [];
  return filter(upgradePlans, (upgrade) => sub.nextRenew.upgrades.includes(upgrade.id));
});

function addUpgradeDowngrade(upgradeId: string) {
  if (subscriptionDowngrade.upgrades.includes(upgradeId)) {
    subscriptionDowngrade.upgrades = subscriptionDowngrade.upgrades.filter((id) => id !== upgradeId);
  } else {
    subscriptionDowngrade.upgrades.push(upgradeId);
  }
}

// =======================
// NEXT PERIOD CONFIG

const nextPeriodConfig = computed(() => {
  if (mode == "choose") {
    return {
      seats: ctx.subscription?.nextRenew.seats || 0,
      upgrades: ctx.subscription?.nextRenew.upgrades || [],
      years: ctx.subscription?.nextRenew.years || 1
    };
  }
  if (mode == "upgrade") {
    const upgrades = (ctx.subscription?.nextRenew.upgrades ?? []).slice();
    for (const upgrade of subscriptionUpgrade.upgrades) {
      if (!upgrades.includes(upgrade)) {
        upgrades.push(upgrade);
      }
    }
    return {
      seats: (ctx.subscription?.nextRenew.seats || 0) + subscriptionUpgrade.seats,
      upgrades: upgrades,
      years: ctx.subscription?.nextRenew.years || 1
    };
  }
  if (mode == "downgrade") {
    let seats = ctx.subscription?.nextRenew.seats ?? 0;
    const upgrades = (ctx.subscription?.nextRenew.upgrades ?? []).slice();
    for (const upgrade of subscriptionDowngrade.upgrades) {
      if (upgrades.includes(upgrade)) {
        upgrades.splice(upgrades.indexOf(upgrade), 1);
      }
    }
    return {
      seats: seats - subscriptionDowngrade.seats,
      upgrades: upgrades,
      years: ctx.subscription?.nextRenew.years || 1
    };
  }
  return {
    seats: 0,
    upgrades: [],
    years: ctx.subscription?.nextRenew.years || 1
  };
});

// =======================
// ORDER

const ordering = ref(false);

async function sendOrder(type: "upgrade" | "downgrade" = "upgrade") {
  if (ctx.subscription == null) {
    showError("Organization not loaded");
    return;
  }
  try {
    ordering.value = true;
    const order: {
      seats?: number | null,
      upgrades: string[]
    } = {
      seats: nextPeriodConfig.value.seats,
      upgrades: nextPeriodConfig.value.upgrades
    };

    await subscriptionStore.modifySubscription(ctx.subscription, order);
    
    ordering.value = false;

    router.push({
      name: "subscription-details",
      params: { subscriptionId: subscriptionId }
    });

    if (type == "upgrade") {
      await showMessage(t("messages.orderSuccessful"));
    } else {
      await showMessage(t("messages.downgradeSuccessful"));
    }
  } catch (error) {
    showError(error as Error);
  } finally {
    ordering.value = false;
  }
}


// =======================
// CANCELING


async function cancelSubscription() {
  if (
    (await showConfirm(
      t("questions.cancelSubscription"),
      t("actions.disableRenewal")
    )) == false
  ) {
    return;
  }

  try {
    const updatedSubscription = await subscriptionStore.cancelSubscription(
      subscriptionId
    );
    console.log("Subscription canceled", updatedSubscription);
    toast.success(t("messages.subscriptionCanceled"));
  } catch (e) {
    console.error(e);
    showError(e as Error);
  }
}
async function reactivateSubscriptionRenewal() {
  try {
    await subscriptionStore.reactivateSubscriptionRenewal(subscriptionId);
    toast.success(t("messages.subscriptionRenewalReactivated"));
  } catch (e) {
    console.error(e);
    showError(e as Error);
  }
}

// ========================
// HELPERS

const includeVat = computed(() => {
  return ctx.subscription?.billingAddress.country == "DE" || ctx.subscription?.billingAddress.company == null;
});

function formatDateString(dateString?: string | null) {
  if (dateString == null) return "–";
  const d = new Date(dateString);
  return formatDate(d, "D");
}
</script>

<style lang="scss">
.subscription-modify-view {
  &__main {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 25rem;
    gap: 2rem;
  }

  &__sheet {
    align-self: start;
    display: flex;
    gap: 1.5rem;
    flex-direction: column;
    padding: 1.5rem;
    background-color: var(--color-background-deep);
    border-radius: 5px;
    box-shadow: 0 2px 10px var(--color-shadow-1);
  }

  &__header {
    --p: 2rem;
    --m: calc(var(--p) * -0.75);
    padding: var(--p);
    margin-top: var(--m);
    margin-left: var(--m);
    margin-right: var(--m);
    background-color: var(--color-background);
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;

    p {
      margin-bottom: 0;
      color: var(--color-text-moderate);
    }
  }

  &__now-due {
    background-color: var(--color-background-deep);
    border-radius: 5px;
    box-shadow: 0 2px 10px var(--color-shadow-1);
    padding: 1.5rem;
    margin-inline: -1.5rem;
  }

  &__priceheading {
    font-size: 1rem;
    appearance: none;
    background: none;
    border: none;
    padding: 0;
    font-weight: 700;
    font-family: var(--font-family-ui);
    display: flex;
    width: 100%;
    text-align: left;
    gap: 0.5rem;
    align-items: end;
  }
  &__billingdate {
    font-size: 0.875rem;
    color: var(--color-text-medium);
    padding-bottom: 1px;
  }

  &__address {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }

  &__line {
    padding: 0.25rem 0;
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 1rem;
    text-align: right;
    align-items: flex-end;

    strong {
      font-weight: 600;
    }
    & + & {
      border-top: 1px solid var(--color-border);
    }
  }

  &__pricefooter {
    font-size: 1rem;
    color: var(--color-text-medium);
  }

  &__buttonsrow {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap; 
  }
}
</style>
